<template>
	<div>
		<Loader v-model="loading"></Loader>
		<DocumentCropper
			v-if="cropping"
			:image-data="rawFrameForCrop"
			:initial-corners="initialCornersPercent"
			@cropped="handleCropped"
			@cancel="handleCropCancel"
		/>
		<Teleport to="body">
			<div class="camera-fullscreen" v-show="displayCamera" ref="photo">
				<div class="video-stage">
					<video ref="video" id="video" playsinline muted></video>
					<canvas ref="overlay" id="overlay"></canvas>
				</div>
				<canvas ref="canvas" id="canvas"></canvas>
				<canvas ref="detect" id="detect"></canvas>

				<button class="camera-close" @click.prevent="closeCamera()">
					<i class="fas fa-times"></i>
				</button>

				<div class="photo-button" @click.prevent="extract()">
					<div class="circle"></div>
					<div class="ring"></div>
				</div>
			</div>
		</Teleport>
		<div class="container py-4">
			<div class="cards-grid">
				<button
					v-if="getIsDev"
					@click="captureAuto()"
					id="capture"
					type="button"
					class="dashboard-card scan-card">
					<div
						class="card-icon restaurant-client d-flex align-items-center justify-content-center rounded-3 me-3"
						style="width: 48px; height: 48px;">
						<i class="fas fa-file-import fs-4"></i>
					</div>
					<div class="text-start">
						<h3 class="h5 mb-1">Capture auto</h3>
						<p class="mb-0 text-muted small">Debug</p>
					</div>
				</button>
				<button
					@click="requestFullScreen()"
					id="capture"
					type="button"
					class="dashboard-card scan-card">
					<div
						class="card-icon fuel d-flex align-items-center justify-content-center rounded-3 me-3"
						style="width: 48px; height: 48px;">
						<i class="fas fa-camera fs-4"></i>
					</div>
					<div class="text-start">
						<h3 class="h5 mb-1">Capturer</h3>
						<p class="mb-0 text-muted small">Scanner votre note de frais</p>
					</div>
				</button>
				<button
					@click="triggerFileImport()"
					id="import"
					type="button"
					class="dashboard-card scan-card">
					<div
						class="card-icon misc d-flex align-items-center justify-content-center rounded-3 me-3"
						style="width: 48px; height: 48px;">
						<i class="fas fa-file-upload fs-4"></i>
					</div>
					<div class="text-start">
						<h3 class="h5 mb-1">Importer</h3>
						<p class="mb-0 text-muted small">Choisir une image ou un PDF existant</p>
					</div>
				</button>
				<input
					ref="fileInput"
					type="file"
					accept="image/*,application/pdf"
					class="d-none"
					@change="handleFileImport"
				>
			</div>
			<n-button @click="back()" class="me-2">
				<template #icon>
					<i class="fas fa-arrow-left me-2"></i>
				</template>
				Retour
			</n-button>
		</div>
	</div>
</template>

<script>
import Loader from "../Loader.vue";
import DocumentCropper from "./DocumentCropper.vue";
import jscanify from "jscanify/src/jscanify.js";
import tmpImg from "../../noteFraisBase64.json";
import {mapGetters} from "vuex";
import {NButton} from 'naive-ui';
import {logError, logInfo} from "../../plugins/logger.js";

export default {
	components: {
		Loader,
		DocumentCropper,
		NButton
	},
	data() {
		return {
			width: window.innerWidth,
			height: window.innerHeight,
			result: null,
			displayCamera: false,
			cropping: false,
			rawFrameForCrop: null,
			initialCornersPercent: null,
			loading: false,
			scanner: null,
			canvasCtx: null,
			detectCtx: null,
			overlayCtx: null,
			rafId: null,
			detecting: false,
			autoCapture: true,
			stableFrames: 0,
			capturing: false,
			lastCorners: null,
			// consecutive steady frames before auto-fire (~0.5s @ 60fps)
			STABLE_THRESHOLD: 12,
			// per-corner max move (detect-canvas px) to count as steady
			STABLE_TOLERANCE: 6,
			// la boucle de détection tourne à 60fps : on ne remonte l'erreur qu'une fois
			detectErrorLogged: false,
		}
	},
	mounted() {
		this.scanner = new jscanify();
		this.canvasCtx = this.$refs.canvas.getContext("2d", {willReadFrequently: true});
		this.detectCtx = this.$refs.detect.getContext("2d", {willReadFrequently: true});
		this.overlayCtx = this.$refs.overlay.getContext("2d");
	},
	computed: {
		...mapGetters(['getIsDev']),
	},
	methods: {
		captureAuto() {
			this.$emit('changePath', 'dataValidation');
			this.$store.dispatch('setBase64Img', tmpImg.base64);
		},
		triggerFileImport() {
			this.$refs.fileInput.click();
		},
		handleFileImport(event) {
			const file = event.target.files[0];
			event.target.value = '';
			if (!file) return;

			const reader = new FileReader();
			reader.onload = () => {
				const dataUrl = reader.result;
				if (file.type === 'application/pdf') {
					// Pas de recadrage possible sur un PDF : on l'envoie tel quel.
					this.$emit('input', dataUrl);
					this.$emit('changePath', 'dataValidation');
					this.$store.dispatch('setBase64Img', dataUrl);
				} else {
					this.rawFrameForCrop = dataUrl;
					this.initialCornersPercent = null;
					this.cropping = true;
				}
			};
			reader.readAsDataURL(file);
		},
		requestFullScreen() {
			this.stableFrames = 0;
			this.capturing = false;
			this.lastCorners = null;
			this.displayCamera = true;
			document.body.classList.add('camera-open');
			this.enterNativeFullscreen();

			this.detectErrorLogged = false;
			logInfo('capture.camera', 'ouverture caméra');

			navigator.mediaDevices.getUserMedia({
				audio: false,
				video: {
					facingMode: "environment",
					width: {ideal: 1920},
					height: {ideal: 1080},
				}
			}).then((stream) => {
				this.$refs.video.srcObject = stream;
				this.$refs.video.onloadedmetadata = () => {
					this.$refs.video.play();

					const vw = this.$refs.video.videoWidth;
					const vh = this.$refs.video.videoHeight;

					// Full-res grab canvas — only used at extract time.
					this.$refs.canvas.width = vw;
					this.$refs.canvas.height = vh;

					// Downscale detection canvas: ~480px wide. ~16x fewer pixels
					// than 1920x1080 => OpenCV contour pass is cheap.
					const dw = 480;
					const dh = Math.round(dw * vh / vw);
					this.$refs.detect.width = dw;
					this.$refs.detect.height = dh;

					// Overlay matches detection resolution — corners drawn in
					// detect-canvas coords, CSS scales overlay to match video.
					this.$refs.overlay.width = dw;
					this.$refs.overlay.height = dh;

					logInfo('capture.camera', 'flux vidéo prêt', {video: `${vw}x${vh}`, detect: `${dw}x${dh}`});
					this.detectLoop();
				};
			}).catch((e) => {
				// Permission refusée, caméra occupée, pas de contexte sécurisé,
				// ou contrainte facingMode non satisfaite : sans ce catch l'écran
				// reste noir sans aucun message.
				logError('capture.getUserMedia', e, {});
				this.closeCamera();
				alert("Impossible d'accéder à la caméra : " + (e && e.message ? e.message : e));
			});
		},
		detectLoop() {
			this.rafId = requestAnimationFrame(this.detectLoop);
			if (this.detecting) return; // skip frame if previous detect still running
			this.detecting = true;
			try {
				const d = this.$refs.detect;
				this.detectCtx.drawImage(this.$refs.video, 0, 0, d.width, d.height);
				const img = cv.imread(d);
				const maxContour = this.scanner.findPaperContour(img);
				this.overlayCtx.clearRect(0, 0, d.width, d.height);
				let cornersOk = false;
				let c = null;
				if (maxContour) {
					c = this.scanner.getCornerPoints(maxContour, img);
					if (c.topLeftCorner && c.topRightCorner && c.bottomLeftCorner && c.bottomRightCorner) {
						cornersOk = this.isValidQuad(c, d.width, d.height);
					}
				}
				img.delete();

				if (cornersOk) {
					const steady = this.cornersSteady(c);
					this.lastCorners = c;
					if (steady) this.stableFrames++;
					else this.stableFrames = 1;

					// Green once locked, orange while still stabilizing.
					const locked = this.stableFrames >= this.STABLE_THRESHOLD;
					this.drawQuad(c, locked ? "#22c55e" : "orange");

					if (locked && this.autoCapture && !this.capturing) {
						this.capturing = true;
						this.extract();
					}
				} else {
					this.stableFrames = 0;
					this.lastCorners = null;
				}
			} catch (e) {
				// Erreur transitoire sur une frame : on continue de boucler,
				// mais on remonte la première occurrence (OpenCV pas prêt, mémoire, etc.).
				if (!this.detectErrorLogged) {
					this.detectErrorLogged = true;
					logError('capture.detectLoop', e, {cv_defined: typeof cv !== 'undefined'});
				}
			} finally {
				this.detecting = false;
			}
		},
		drawQuad(c, color) {
			const ctx = this.overlayCtx;
			ctx.strokeStyle = color;
			ctx.lineWidth = 3;
			ctx.beginPath();
			ctx.moveTo(c.topLeftCorner.x, c.topLeftCorner.y);
			ctx.lineTo(c.topRightCorner.x, c.topRightCorner.y);
			ctx.lineTo(c.bottomRightCorner.x, c.bottomRightCorner.y);
			ctx.lineTo(c.bottomLeftCorner.x, c.bottomLeftCorner.y);
			ctx.closePath();
			ctx.stroke();
		},
		// Reject junk contours: paper must fill enough of frame, else tiny
		// noise quads trigger false auto-capture.
		isValidQuad(c, w, h) {
			const {topLeftCorner: tl, topRightCorner: tr, bottomLeftCorner: bl, bottomRightCorner: br} = c;
			const quadW = Math.max(tr.x - tl.x, br.x - bl.x);
			const quadH = Math.max(bl.y - tl.y, br.y - tr.y);
			if (quadW < w * 0.35 || quadH < h * 0.35) return false;
			return true;
		},
		// Steady = all 4 corners moved < threshold px vs last frame (detect-canvas coords).
		cornersSteady(c) {
			if (!this.lastCorners) return false;
			const keys = ['topLeftCorner', 'topRightCorner', 'bottomLeftCorner', 'bottomRightCorner'];
			for (const k of keys) {
				const dx = c[k].x - this.lastCorners[k].x;
				const dy = c[k].y - this.lastCorners[k].y;
				if (Math.hypot(dx, dy) > this.STABLE_TOLERANCE) return false;
			}
			return true;
		},
		async extract() {
			this.stopDetect();

			const cw = this.$refs.canvas.width;
			const ch = this.$refs.canvas.height;

			try {
				// Grab a fresh full-res frame — the raw photo, not yet cropped.
				this.canvasCtx.drawImage(this.$refs.video, 0, 0);

				// La photo d'abord : si la détection OpenCV échoue ensuite, l'utilisateur
				// garde son image et recadre à la main plutôt que de rester bloqué.
				this.rawFrameForCrop = this.$refs.canvas.toDataURL("image/jpeg", 0.92);
				if (!this.rawFrameForCrop || this.rawFrameForCrop.length < 1000) {
					throw new Error(`toDataURL vide (${cw}x${ch})`);
				}

				this.initialCornersPercent = this.detectCornersPercent(cw, ch);

				logInfo('capture.extract', 'photo capturée', {
					size: `${cw}x${ch}`,
					kb: Math.round(this.rawFrameForCrop.length / 1024),
					auto_corners: !!this.initialCornersPercent,
				});
			} catch (e) {
				// Sans ce catch, une exception ici laissait la caméra ouverte, le
				// scroll du body bloqué et l'écran figé : plus aucun bouton cliquable.
				logError('capture.extract', e, {size: `${cw}x${ch}`, cv_defined: typeof cv !== 'undefined'});
				await this.stopCamera();
				alert("La capture a échoué sur cet appareil. Réessayez, ou utilisez « Importer ».");
				return;
			}

			await this.stopCamera();
			this.cropping = true;
		},
		// Détection auto des coins pour pré-remplir le recadrage manuel.
		// Best-effort : retourne null si OpenCV n'aboutit pas, DocumentCropper
		// retombe alors sur sa propre détection par luminance.
		detectCornersPercent(cw, ch) {
			if (typeof cv === 'undefined' || typeof cv.imread !== 'function') return null;

			let img = null;
			try {
				img = cv.imread(this.$refs.canvas);
				const maxContour = this.scanner.findPaperContour(img);
				if (!maxContour) return null;

				const c = this.scanner.getCornerPoints(maxContour, img);
				if (!c.topLeftCorner || !c.topRightCorner || !c.bottomLeftCorner || !c.bottomRightCorner) return null;

				return {
					topLeft: {x: c.topLeftCorner.x / cw * 100, y: c.topLeftCorner.y / ch * 100},
					topRight: {x: c.topRightCorner.x / cw * 100, y: c.topRightCorner.y / ch * 100},
					bottomRight: {x: c.bottomRightCorner.x / cw * 100, y: c.bottomRightCorner.y / ch * 100},
					bottomLeft: {x: c.bottomLeftCorner.x / cw * 100, y: c.bottomLeftCorner.y / ch * 100},
				};
			} catch (e) {
				logError('capture.detectCorners', e, {size: `${cw}x${ch}`});
				return null;
			} finally {
				if (img) {
					try { img.delete(); } catch (e) { /* Mat déjà libéré */ }
				}
			}
		},
		handleCropped(dataUrl) {
			this.cropping = false;
			this.$emit('input', dataUrl);
			this.$emit('changePath', 'dataValidation');
			this.$store.dispatch('setBase64Img', dataUrl);
		},
		handleCropCancel() {
			this.cropping = false;
			this.rawFrameForCrop = null;
			this.requestFullScreen();
		},
		enterNativeFullscreen() {
			// Android Chrome/Firefox + desktop: hides browser UI (address/nav bar).
			// iOS Safari has NO element Fullscreen API — the fixed overlay is the
			// only fallback there. Errors ignored: overlay covers viewport regardless.
			const el = this.$refs.photo;
			const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
			if (req) {
				try { req.call(el); } catch (e) { /* unsupported/denied — overlay still covers screen */ }
			}
		},
		// Renvoie une promesse résolue quand le plein écran natif est réellement
		// sorti. Tant qu'on y est, le viewport annoncé par le navigateur est celui
		// du plein écran : afficher le recadrage avant la sortie le fait calculer
		// sur une hauteur trop grande, et ses boutons se retrouvent hors écran.
		exitNativeFullscreen() {
			const isFs = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
			if (!isFs) return Promise.resolve();

			const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
			if (!exit) return Promise.resolve();

			return new Promise((resolve) => {
				// Filet : certains navigateurs n'émettent pas l'événement.
				const timer = setTimeout(done, 600);

				function done() {
					clearTimeout(timer);
					document.removeEventListener('fullscreenchange', done);
					document.removeEventListener('webkitfullscreenchange', done);
					resolve();
				}

				document.addEventListener('fullscreenchange', done);
				document.addEventListener('webkitfullscreenchange', done);

				try {
					const r = exit.call(document);
					if (r && typeof r.then === 'function') r.then(done).catch(done);
				} catch (e) {
					done();
				}
			});
		},
		stopDetect() {
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
			this.detecting = false;
		},
		// Asynchrone : attend la sortie effective du plein écran natif.
		async stopCamera() {
			// Chaque étape est isolée : si l'une échoue (fullscreen refusé, piste
			// déjà coupée), les suivantes doivent quand même s'exécuter, sinon
			// l'overlay et le blocage du scroll restent en place.
			try { await this.exitNativeFullscreen(); } catch (e) { logError('capture.stopCamera.fullscreen', e, {}); }
			try { this.stopDetect(); } catch (e) { logError('capture.stopCamera.detect', e, {}); }
			try {
				const stream = this.$refs.video && this.$refs.video.srcObject;
				if (stream) {
					stream.getTracks().forEach(t => t.stop());
					this.$refs.video.srcObject = null;
				}
			} catch (e) {
				logError('capture.stopCamera.stream', e, {});
			}
			this.displayCamera = false;
			this.capturing = false;
			document.body.classList.remove('camera-open');
		},
		async closeCamera() {
			await this.stopCamera();
		},
		back() {
			this.stopCamera();
			this.$emit('changePath', 'categories');
		},
	},
	beforeUnmount() {
		this.stopCamera();
	}
}

</script>

<style scoped>
.capture-container {
	min-height: 100vh;
	background-color: #f8f9fa;
}

/* Fullscreen overlay — teleported to body, escapes any transformed ancestor.
   F11 equivalent for mobile: real Fullscreen API is unreliable (iOS Safari
   has no element fullscreen), so we cover the viewport with position:fixed. */
.camera-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 2000;
	width: 100vw;
	height: 100vh;
	height: 100dvh; /* dynamic vh — accounts for mobile URL bar */
	background-color: #000;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.video-stage {
	position: relative;
	max-width: 100%;
	max-height: 100%;
	display: flex;
}

.camera-fullscreen #video {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
	display: block;
}

/* Overlay sits exactly over the video box, scaled to its rendered size.
   Detection draws in detect-canvas coords; CSS stretches to match. */
.camera-fullscreen #overlay {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
}

#canvas, #detect {
	display: none;
}

.camera-close {
	position: absolute;
	top: env(safe-area-inset-top, 16px);
	right: 16px;
	width: 44px;
	height: 44px;
	border: none;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.5);
	color: #fff;
	font-size: 20px;
	z-index: 20;
	cursor: pointer;
}

.photo-button {
	width: 80px;
	height: 80px;
	bottom: 20px;
	left: 50%;
	margin-left: -40px;
	position: absolute;
	z-index: 10;
	cursor: pointer;
}

.circle {
	position: absolute;
	top: 12%;
	left: 12%;
	bottom: 12%;
	right: 12%;
	border-radius: 100%;
	background-color: #ffffff;
	opacity: 0;
	transition: opacity 0.2s ease;
}

.ring {
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	border-radius: 100%;
	border: 4px solid #ffffff;
	opacity: 0.9;
	transition: all 0.2s ease;
}

.photo-button:hover .circle {
	opacity: 0.3;
}

.photo-button:active .ring {
	transform: scale(0.95);
	opacity: 1;
}

.photo-button:active .circle {
	opacity: 0.5;
}

</style>

<style>
/* Unscoped: body isn't a component element, scoped selectors can't reach it.
   Lock page scroll while camera overlay is open. */
body.camera-open {
	overflow: hidden;
}
</style>